import { z } from 'zod';
import { PASSWORD_MAX_LENGTH } from '@bekebi/domain';

/**
 * First-run setup (DECISIONS.md, 2026-10-14): creating the very first Admin through the portal.
 * Possible only while no Admin exists, and only with the one-time setup secret the operator put in
 * the server environment (PORTAL_SETUP_TOKEN). Once any Admin exists, setup is permanently off and
 * accounts are created by an Admin (POST /api/users).
 */

/** Whether the portal still needs its first Admin. Says nothing else about the server. */
export const setupStatusSchema = z.object({
  /** True only while no Admin account exists. */
  setupRequired: z.boolean(),
  /** True only when setup is required and the server has a setup secret configured. */
  setupAvailable: z.boolean(),
});
export type SetupStatus = z.infer<typeof setupStatusSchema>;

/** Strict: no other field (such as a role) is accepted — the account is always an Admin. */
export const setupAdminRequestSchema = z.strictObject({
  /**
   * The operator's one-time setup secret. Checked server-side; never stored or echoed. At most
   * 256 characters: the same limit as PORTAL_SETUP_TOKEN itself (packages/config).
   */
  setupToken: z.string().min(1).max(256),
  email: z.string().trim().email().max(254),
  /** Checked against the password policy server-side, hashed (Argon2id), never stored in clear. */
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type SetupAdminRequest = z.infer<typeof setupAdminRequestSchema>;

/** The created Admin, identified for the operator. No session is started: sign in next. */
export const setupAdminResultSchema = z.object({
  email: z.string(),
  bekebiUserId: z.string(),
});
export type SetupAdminResult = z.infer<typeof setupAdminResultSchema>;
